import { FileUploadAttributes } from "@copetence/shared";
import NotificationBanner from "components/banner/Banner.comp";
import envC from "constants/envConfig";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import s from "../styles/loadTicket.module.scss";

const LoadTicket = () => {
  const { fileId } = useParams<{ fileId: string }>();
  const [file, setFile] = useState<Blob | null>(null);
  const [fileObject, setFileObject] = useState<FileUploadAttributes | null>(
    null,
  );

  const [privateKey, setPrivateKey] = useState<string>("");
  const [storedKey, setStoredKey] = useState<CryptoKey | null>(null);
  const [isDecrypting, setIsDecrypting] = useState<boolean>(false);
  const [decryptedFile, setDecryptedFile] = useState<Blob | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  // Auto-dismiss notifications after 5 seconds
  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(null), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(false), 5000);
    return () => clearTimeout(timer);
  }, [success]);

  // Load file from backend
  useEffect(() => {
    if (!fileId) return;

    setIsLoading(true);
    fetch(`${envC.REACT_APP_API_URL}/fileUploads/load/${fileId}`, {
      method: "GET",
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("Failed to load file");

        // Get filename from Content-Disposition header if available
        const fileName =
          response.headers.get("X-File-Name") || `file-${fileId}`;
        const fileType = response.headers.get("X-File-Type") || "image/png";
        const fileSize = response.headers.get("Content-Length") || "0";

        console.log("Headers", response.headers);

        console.log("FileName", fileName);
        setFileObject({
          id: fileId,
          fileName: fileName,
          fileType: fileType,
          fileSize: parseInt(fileSize, 10),
        });

        console.log("response", response);

        const blob = await response.blob();
        setFile(blob);
      })
      .catch((err) => {
        setError(`Error loading file: ${err.message}`);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [fileId]);

  // Check if private key exists in storage
  useEffect(() => {
    const storedPrivateKey = localStorage.getItem("ticketing_private_key");
    if (storedPrivateKey) {
      setPrivateKey(storedPrivateKey);
    }
  }, []);

  useEffect(() => {
    if (!file || !fileObject || !privateKey || isDecrypting) return;
    decryptFile();
  }, [file, fileObject, privateKey]);

  const hasDownloaded = useRef(false);

  useEffect(() => {
    if (decryptedFile && !hasDownloaded.current) {
      hasDownloaded.current = true;
      downloadDecryptedFile();
    }
  }, [decryptedFile]);

  /*
  Import a PEM encoded RSA private key, to use for RSA-PSS signing.
  Takes a string containing the PEM encoded key, and returns a Promise
  that will resolve to a CryptoKey representing the private key.
  */
  function importPrivateKey(pem: string): Promise<CryptoKey> {
    try {
      // Extract content between header and footer, removing all whitespace
      const pemHeader = "-----BEGIN PRIVATE KEY-----";
      const pemFooter = "-----END PRIVATE KEY-----";
      const pemContents = pem
        .substring(
          pem.indexOf(pemHeader) + pemHeader.length,
          pem.indexOf(pemFooter),
        )
        .replace(/\s/g, "");

      // base64 decode the string to get the binary data
      const binaryDerString = window.atob(pemContents);
      // convert from a binary string to an ArrayBuffer
      const binaryDer = str2ab(binaryDerString);

      return window.crypto.subtle.importKey(
        "pkcs8",
        binaryDer,
        {
          name: "RSA-OAEP",
          hash: "SHA-256",
        },
        true,
        ["decrypt"],
      );
    } catch (err) {
      console.error("Private key import error:", err);
      throw err;
    }
  }

  /*
  Convert a string into an ArrayBuffer
  from https://developers.google.com/web/updates/2012/06/How-to-convert-ArrayBuffer-to-and-from-String
  */
  function str2ab(str: string) {
    const buf = new ArrayBuffer(str.length);
    const bufView = new Uint8Array(buf);
    for (let i = 0, strLen = str.length; i < strLen; i++) {
      bufView[i] = str.charCodeAt(i);
    }
    return buf;
  }

  const decryptFile = async () => {
    if (!file || !privateKey) {
      return;
    }

    setIsDecrypting(true);
    setError(null);

    try {
      // Import key if not already done
      let key = storedKey;
      if (!key) {
        key = await importPrivateKey(privateKey);
        setStoredKey(key);
        localStorage.setItem("ticketing_private_key", privateKey);
      }

      // Get file content as ArrayBuffer
      const fileBuffer = await file.arrayBuffer();
      console.log("fileBuffer", fileBuffer);

      // Parse the combined array format:
      // [12 bytes IV][4 bytes key length][encrypted key][encrypted file]

      // Extract the IV (first 12 bytes)
      // const iv = new Uint8Array([113, 88, 218, 169, 51, 65, 48, 186, 31, 152, 222, 96]);

      const iv = new Uint8Array(fileBuffer.slice(0, 12));
      console.log("iv", iv);

      // Extract the encrypted key length (4 bytes)
      const keyLengthView = new DataView(fileBuffer.slice(12, 16));
      const encryptedKeyLength = keyLengthView.getUint32(0, true);

      // Extract the encrypted key
      const encryptedKeyBuffer = fileBuffer.slice(16, 16 + encryptedKeyLength);

      // Extract the encrypted file data
      const encryptedFile = new Uint8Array(
        fileBuffer.slice(16 + encryptedKeyLength),
      );

      console.log("encryptedFile", encryptedFile);
      // Step 1: Decrypt the AES key with RSA private key
      const decryptedKeyBuffer = await crypto.subtle.decrypt(
        { name: "RSA-OAEP" },
        key,
        encryptedKeyBuffer,
      );

      console.log("decryptedKeyBuffer", decryptedKeyBuffer);

      // Step 2: Import the decrypted AES key
      const aesKey = await crypto.subtle.importKey(
        "raw",
        decryptedKeyBuffer,
        {
          name: "AES-GCM",
          length: 256,
        },
        false,
        ["decrypt"],
      );

      // Step 3: Decrypt the file with the AES key
      const decryptedBuffer = await crypto.subtle.decrypt(
        {
          name: "AES-GCM",
          iv: iv,
        },
        aesKey,
        encryptedFile,
      );

      // Create a blob from the decrypted data
      const decrypted = new Blob([decryptedBuffer]);
      setDecryptedFile(decrypted);
      setSuccess(true);
    } catch (err: any) {
      setError(`Decryption failed: ${err.message}`);
    } finally {
      setIsDecrypting(false);
    }
  };

  // Handle download of decrypted file
  const downloadDecryptedFile = () => {
    if (!decryptedFile) {
      setError("No decrypted file to download");
      return;
    }

    // Make sure we have a filename, fall back to a default if not
    const downloadName = fileObject?.fileName || `decrypted-file-${fileId}.bin`;

    // Try to determine MIME type from filename extension
    const fileExtension = downloadName.split(".").pop()?.toLowerCase();
    const mimeType = fileExtension
      ? fileObject?.fileType
      : "application/octet-stream";

    // Create a new blob with the correct mime type
    const blobWithMimeType = new Blob([decryptedFile], { type: mimeType });

    // Create object URL
    const url = URL.createObjectURL(blobWithMimeType);

    console.log("Downloading file:", {
      name: downloadName,
      size: blobWithMimeType.size,
      type: blobWithMimeType.type,
      url,
    });

    // Create and trigger download link
    const a = document.createElement("a");
    a.href = url;
    a.download = downloadName;
    document.body.appendChild(a);
    a.click();

    // Clean up
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  };

  if (isLoading) {
    return (
      <div className={s.container}>
        <div className={s.centeredContent}>
          <div className={s.spinner} />
        </div>
      </div>
    );
  }

  return (
    <div className={s.container}>
      <div className={s.centeredContent}>
        {!isLoading && !storedKey && !decryptedFile && (
          <div className={s.keyInputCard}>
            <h2 className={s.cardTitle}>Secure File Decryption</h2>

            <div className={s.inputContainer}>
              <label className={s.label}>Private Key</label>
              <textarea
                className={s.input}
                placeholder="Paste your private key here..."
                value={privateKey}
                onChange={(e) => setPrivateKey(e.target.value)}
                rows={6}
              />
              <p className={s.hint}>
                Enter your private key to decrypt the file. It will be stored
                locally for future use.
              </p>
            </div>
            <button
              className={s.submitBTN}
              onClick={decryptFile}
              disabled={isDecrypting || !privateKey}
            >
              {isDecrypting ? "Decrypting..." : "Decrypt File"}
            </button>
          </div>
        )}

        {error && <NotificationBanner message={error} type="error" />}
        {success && (
          <NotificationBanner
            message="File decrypted successfully!"
            type="success"
          />
        )}

        {decryptedFile && (
          <div className={s.keyInputCard}>
            <h3 className={s.successTitle}>Decryption Successful!</h3>
            <p className={s.successText}>Your file is ready to download.</p>
            <button className={s.downloadBTN} onClick={downloadDecryptedFile}>
              Download Decrypted File
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoadTicket;
